import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Button, Icon, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import { useIsDesktop } from '../../../hooks/use-media-query';
import { useThemeEditor } from '../context/theme-editor-context';
import type { FileType, ThemeFile } from '../types/theme';

const TYPE_ORDER: FileType[] = ['email', 'pdf', 'section', 'language'];

function typeLabel(type: FileType): string {
  return t(`features:themes.pages.theme_editor_page.file_types.${type}`);
}

export function EditorSidebar({
  onAddFile,
  open,
  onClose,
}: {
  onAddFile: () => void;
  open: boolean;
  onClose: () => void;
}) {
  const { theme, openFiles, tabOrder, focusedFile, openFile } = useThemeEditor();
  const { navigationHandler } = useHandlers();
  const isDesktop = useIsDesktop();
  const [expanded, setExpanded] = useState<Partial<Record<FileType, boolean>>>({});

  const fileTree = useMemo(() => {
    const tree: Partial<Record<FileType, ThemeFile[]>> = {};
    for (const file of theme?.files ?? []) {
      const group = tree[file.type] ?? [];
      group.push(file);
      tree[file.type] = group;
    }
    return tree;
  }, [theme]);

  // Auto-expand the type group of any open tab.
  useEffect(() => {
    const types = tabOrder
      .map((id) => openFiles[id]?.type)
      .filter((type): type is FileType => Boolean(type));
    if (types.length === 0) {
      return;
    }
    setExpanded((current) => {
      const next = { ...current };
      for (const type of types) {
        next[type] = true;
      }
      return next;
    });
  }, [tabOrder, openFiles]);

  function toggle(type: FileType) {
    setExpanded((current) => ({ ...current, [type]: !current[type] }));
  }

  function handleSelect(file: ThemeFile) {
    void openFile(file);
    // On mobile the sidebar is an overlay drawer; close it once a file is picked.
    if (!isDesktop) {
      onClose();
    }
  }

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label={t('features:themes.pages.theme_editor_page.close_files')}
          className="absolute inset-0 z-30 bg-gray-600/75 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={cn(
          'absolute inset-y-0 start-0 z-40 flex w-64 max-w-[80%] shrink-0 flex-col border-spark-border-default border-e bg-white transition-transform duration-200',
          'lg:static lg:z-0 lg:max-w-none lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <header className="flex shrink-0 items-center gap-2 border-spark-border-default border-b px-3 py-2">
          <Button
            variant="bare"
            icon="back"
            ariaLabel={t('features:themes.pages.theme_editor_page.back')}
            onClick={() => navigationHandler.navigate('/configuration/theme')}
          />
          <span className="min-w-0 flex-1">
            <Text bold truncate>
              {theme?.name ?? ''}
            </Text>
          </span>
        </header>

        <ol className="flex-1 overflow-y-auto py-1">
          {TYPE_ORDER.map((type) => {
            const files = fileTree[type];
            if (!files?.length) {
              return null;
            }
            return (
              <li key={type}>
                <button
                  type="button"
                  onClick={() => toggle(type)}
                  className="flex w-full items-center gap-1.5 px-2 py-2.5 hover:bg-gray-50"
                >
                  <span className={cn('transition-transform', expanded[type] && 'rotate-180')}>
                    <Icon name="chevronDown" size="xs" />
                  </span>
                  <Text bold size="sm" truncate>
                    {typeLabel(type)}
                  </Text>
                </button>
                {expanded[type] && (
                  <ol>
                    {files.map((file) => {
                      const isActive =
                        file.type === focusedFile?.type && file.slug === focusedFile?.slug;
                      return (
                        <li key={file.id}>
                          <button
                            type="button"
                            onClick={() => handleSelect(file)}
                            className={cn(
                              'flex w-full items-center gap-1.5 border-s-2 py-2 ps-7 pe-3 hover:bg-gray-50',
                              isActive
                                ? 'border-platform-primary bg-gray-50 text-platform-primary'
                                : 'border-transparent text-spark-text-secondary',
                            )}
                          >
                            <Icon name="document" size="sm" />
                            <span className="truncate text-sm" title={file.slug}>
                              {file.slug}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </li>
            );
          })}
        </ol>

        <div className="shrink-0 border-spark-border-default border-t p-3">
          <Button variant="secondary" icon="plusCircle" className="w-full" onClick={onAddFile}>
            {t('features:themes.pages.theme_editor_page.add_file_button')}
          </Button>
        </div>
      </aside>
    </>
  );
}
